import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { RelatedObject, SectionDataType } from "@/types/dynamic-detail";
import { ExternalLink } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { Link, useLoaderData, useLocation } from "react-router-dom";
import { DynamicSection } from "./dynamic-tab-content";
import { RelatedList } from "./related-list";

interface DynamicRelatedObjectsProps {
    relatedObjects: RelatedObject[];
    objectApiUrl: string;
    objectId: string;
    objectName?: string;
}

type RelatedObjectModalResponse = {
    modal: NonNullable<RelatedObject["modal"]>;
    detail_url: string | null;
};

type ModalHashPayload = {
    parentObjectKey: string;
    parentId: string;
    relatedName: string;
    relatedId: string;
};

const RELATED_MODAL_HASH_PREFIX = "crm-related-modal";

function encodeHashPart(value: string): string {
    return encodeURIComponent(value);
}

function decodeHashPart(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function buildModalHash(payload: ModalHashPayload): string {
    return [RELATED_MODAL_HASH_PREFIX, encodeHashPart(payload.parentObjectKey), encodeHashPart(payload.parentId), encodeHashPart(payload.relatedName), encodeHashPart(payload.relatedId)].join(":");
}

function parseModalHash(hash: string): ModalHashPayload | null {
    const raw = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!raw) return null;
    const parts = raw.split(":");
    if (parts.length !== 5 || parts[0] !== RELATED_MODAL_HASH_PREFIX) {
        return null;
    }
    return {
        parentObjectKey: decodeHashPart(parts[1]),
        parentId: decodeHashPart(parts[2]),
        relatedName: decodeHashPart(parts[3]),
        relatedId: decodeHashPart(parts[4])
    };
}

function RelatedObjectModal({
    open,
    onOpenChange,
    objectApiUrl,
    objectId,
    relatedObject,
    relatedId,
    fallbackDetailUrl
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    objectApiUrl: string;
    objectId: string;
    relatedObject: RelatedObject | null;
    relatedId: string | null;
    fallbackDetailUrl: string | null;
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [payload, setPayload] = useState<RelatedObjectModalResponse | null>(null);
    const [activeTab, setActiveTab] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !relatedObject || !relatedId) {
            return;
        }

        const fetchModalData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await DataService.client.url(`${objectApiUrl}/${objectId}/related-object-modal/${relatedObject.relationship_name}/${relatedId}/`).get().json<RelatedObjectModalResponse>();
                setPayload(data);
                const firstTab = data.modal?.tabs?.[0];
                setActiveTab(firstTab?.route_path ?? null);
            } catch (err: any) {
                console.error("Error loading related object modal", err);
                setError(err?.message ?? "Erreur lors du chargement de la modal");
                setPayload(null);
            } finally {
                setLoading(false);
            }
        };

        fetchModalData();
    }, [open, relatedObject, relatedId, objectApiUrl, objectId]);

    const currentTab = useMemo(() => payload?.modal?.tabs?.find(tab => tab.route_path === activeTab) ?? payload?.modal?.tabs?.[0], [payload, activeTab]);

    const detailUrl = payload?.detail_url ?? fallbackDetailUrl;
    const sections = (currentTab?.content_type === "sections" ? currentTab.sections : []) as SectionDataType[] | undefined;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] w-[95vw] !max-w-6xl flex-col overflow-hidden">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-3">
                        <DialogTitle>{payload?.modal?.header?.title ?? relatedObject?.label ?? "Detail"}</DialogTitle>
                        {detailUrl && (
                            <Button variant="outline" size="sm" asChild>
                                <Link to={detailUrl} target="_blank" rel="noopener noreferrer">
                                    Ouvrir la page
                                    <ExternalLink className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        )}
                    </div>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                    {loading && <div className="py-8 text-sm text-gray-500">Chargement...</div>}
                    {error && <div className="py-8 text-sm text-red-500">{error}</div>}
                    {!loading && !error && payload?.modal && (
                        <div className="space-y-4">
                            {payload.modal.tabs && payload.modal.tabs.length > 1 && (
                                <div className="flex flex-wrap gap-2 border-b pb-2">
                                    {payload.modal.tabs.map(tab => (
                                        <button
                                            key={tab.route_path}
                                            type="button"
                                            onClick={() => setActiveTab(tab.route_path)}
                                            className={`rounded px-3 py-1 text-sm ${
                                                currentTab?.route_path === tab.route_path ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {currentTab?.content_type === "sections" ? (
                                sections && sections.length > 0 ? (
                                    <div className="space-y-4">
                                        {sections
                                            .filter(section => (section.fields && section.fields.length > 0) || !!section.markdown_content)
                                            .map((section, index) => (
                                                <DynamicSection key={`${section.name}-${index}`} section={section} canUpdate={false} />
                                            ))}
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500">Aucune section configuree</div>
                                )
                            ) : (
                                <div className="text-sm text-gray-500">Le contenu "{currentTab?.content_type}" n&apos;est pas supporte dans la modal.</div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function matchHeaderLinks(
    links: { label: string; href_template: string }[] | undefined,
    ownerId: number | null | undefined,
    canViewMatchAs: boolean
) {
    if (!canViewMatchAs || !ownerId) return links;
    return links?.map(link =>
        link.href_template.startsWith("/alternance/matchs") && !link.href_template.includes("user_id=")
            ? { ...link, href_template: link.href_template.replace("/alternance/matchs", `/alternance/matchs?user_id=${ownerId}`) }
            : link
    );
}

export function DynamicRelatedObjects({ relatedObjects, objectApiUrl, objectId, objectName }: DynamicRelatedObjectsProps) {
    const location = useLocation();
    const { user, has_any_group } = useUser();
    const ownerId = (useLoaderData() as { object?: { owner?: { id?: number | null } | null } }).object?.owner?.id;
    const canViewMatchAs = Boolean(user?.is_super_admin || user?.is_manager) || has_any_group(["app_apprenticeship_admin", "app_crm_admin"]);
    const [selectedRelatedObject, setSelectedRelatedObject] = useState<RelatedObject | null>(null);
    const [selectedRelatedId, setSelectedRelatedId] = useState<string | null>(null);
    const [selectedRelatedDetailUrl, setSelectedRelatedDetailUrl] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const parentObjectKey = objectName || objectApiUrl.replace(/^\/crm\/objects\//, "").replace(/\//g, "_");

    const writeModalHash = (relatedName: string, relatedId: string) => {
        const newHash = buildModalHash({
            parentObjectKey,
            parentId: String(objectId),
            relatedName,
            relatedId
        });
        window.history.replaceState(null, "", `${location.pathname}${location.search}#${newHash}`);
    };

    const clearModalHashIfCurrent = () => {
        const parsed = parseModalHash(location.hash);
        if (!parsed) return;
        if (parsed.parentObjectKey !== parentObjectKey || parsed.parentId !== String(objectId)) return;
        window.history.replaceState(null, "", `${location.pathname}${location.search}`);
    };

    useEffect(() => {
        const parsed = parseModalHash(location.hash);
        if (!parsed) {
            return;
        }
        if (parsed.parentObjectKey !== parentObjectKey || parsed.parentId !== String(objectId)) {
            return;
        }

        const relatedObject = relatedObjects.find(related => related.relationship_name === parsed.relatedName && Boolean(related.modal));
        if (!relatedObject) {
            return;
        }

        const detailUrl = relatedObject.href_template ? relatedObject.href_template.replace("{id}", parsed.relatedId) : null;
        setSelectedRelatedObject(relatedObject);
        setSelectedRelatedId(parsed.relatedId);
        setSelectedRelatedDetailUrl(detailUrl);
        setIsModalOpen(true);
    }, [location.hash, parentObjectKey, objectId, relatedObjects]);

    if (!relatedObjects || relatedObjects.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            {relatedObjects.map((relatedObject, index) => {
                const hasDedicatedPage = Boolean(relatedObject.href_template);
                const hasModal = Boolean(relatedObject.modal);
                const onRowNavigateTo = hasDedicatedPage
                    ? (instance: any, _location?: any, event?: MouseEvent<HTMLTableCellElement>) => {
                          const detailUrl = instance.href || relatedObject.href_template!.replace("{id}", instance.id);
                          if (detailUrl.includes("{")) {
                              return null;
                          }
                          if (!hasModal) {
                              return detailUrl;
                          }

                          if (event?.metaKey || event?.ctrlKey) {
                              return detailUrl;
                          }

                          writeModalHash(relatedObject.relationship_name, String(instance.id));
                          setSelectedRelatedObject(relatedObject);
                          setSelectedRelatedId(String(instance.id));
                          setSelectedRelatedDetailUrl(detailUrl);
                          setIsModalOpen(true);
                          return null;
                      }
                    : undefined;
                return (
                    <RelatedList
                        key={`${relatedObject.relationship_name}-${index}`}
                        objectApiUrl={objectApiUrl}
                        objectId={objectId}
                        relatedName={relatedObject.label}
                        relatedCode={relatedObject.relationship_name}
                        fields_to_display={relatedObject.fields_to_display}
                        clickable_field={hasDedicatedPage ? relatedObject.clickable_field || relatedObject.fields_to_display[0]?.field_name : undefined}
                        onRowNavigateTo={onRowNavigateTo}
                        icon={relatedObject.object_metadata?.icon}
                        color={relatedObject.object_metadata?.color}
                        hide_if_empty={relatedObject.hide_if_empty}
                        objectName={objectName}
                        form={relatedObject.form}
                        actions={relatedObject.actions}
                        rowActions={relatedObject.row_actions}
                        parentRowActions={relatedObject.parent_row_actions}
                        headerLinks={matchHeaderLinks(relatedObject.header_links, ownerId, canViewMatchAs)}
                        childApiUrl={relatedObject.object_metadata?.url ? `/crm/objects/${relatedObject.object_metadata.url}` : undefined}
                    />
                );
            })}
            <RelatedObjectModal
                open={isModalOpen}
                onOpenChange={open => {
                    setIsModalOpen(open);
                    if (!open) {
                        clearModalHashIfCurrent();
                    }
                }}
                objectApiUrl={objectApiUrl}
                objectId={objectId}
                relatedObject={selectedRelatedObject}
                relatedId={selectedRelatedId}
                fallbackDetailUrl={selectedRelatedDetailUrl}
            />
        </div>
    );
}
